import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Instagram, ArrowRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { fetchTrialPosts } from "@/lib/api";
import { TRIAL } from "@/constants/testIds";

export default function InstagramTrial() {
  const { data, isLoading, isError } = useQuery({ queryKey: ["ig-trial"], queryFn: fetchTrialPosts });
  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0] font-mono" data-testid={TRIAL.page}>
      <Nav />

      <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-6">
        <p className="text-[11px] sm:text-xs uppercase tracking-widest text-[#CCFF00] mb-2">Instagram Embed Trial</p>
        <h1 className="text-base sm:text-lg tracking-tight text-[#F5F5F0]">Six real films, embedded live from Instagram.</h1>
        <p className="mt-3 text-[13px] sm:text-sm leading-relaxed text-[#888880] max-w-xl">
          Real EiPi client work played directly here using Instagram's own official embed. Captions and links below
          come straight from the original posts.
        </p>
      </div>

      {isLoading && (
        <div className="py-24 text-center text-xs uppercase tracking-widest text-[#555550]" data-testid="trial-loading-state">
          Loading trial collection…
        </div>
      )}
      {isError && (
        <div className="py-24 text-center text-xs uppercase tracking-widest text-[#888880]" data-testid="trial-error-state">
          Could not load the trial collection. Please retry shortly.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid={TRIAL.grid}>
          {items.map((p) => (
            <Link
              key={p.shortcode}
              to={`/instagram-trial/${p.shortcode}`}
              data-testid={TRIAL.card(p.shortcode)}
              className="group relative flex flex-col border border-[#2A2A2A] hover:border-[#CCFF00] bg-[#151515] overflow-hidden transition-colors"
            >
              <div className="relative w-full aspect-[4/5] bg-black overflow-hidden">
                <img
                  src={p.posterUrl}
                  alt={p.brandName}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale-[0.2] contrast-[1.05] group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <Instagram size={14} className="absolute top-3 right-3 text-white/70 group-hover:text-[#CCFF00] transition-colors" />
                {p.isAIVideo && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/40 bg-black/60 px-2 py-0.5">
                    AI Film
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-[#CCFF00]/90 mb-1">{p.brandCategory}</p>
                  <h2 className="text-base sm:text-lg tracking-tight text-white">{p.brandName}</h2>
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-[#888880] px-4 py-3">
                <span>{p.dateIst ? new Date(p.dateIst).getFullYear() : ""}</span>
                <span className="inline-flex items-center gap-1 group-hover:text-[#CCFF00] transition-colors">
                  Watch <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
